// import axios from '@/axios.js';
import { async } from '@team-decorate/vue-line-login'
import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/firestore'
import 'firebase/storage'
import router from '@/router'
import _ from 'lodash'
import moment from 'moment-timezone'
import { getSessionCachedData } from '@/utils/sessionDataCache'
const axios = require('axios').default
const qs = require('qs')

const PROMOTION_BALANCE_LIMIT = 10

function toCreditNumber (value) {
  const amount = Number(String(value === undefined || value === null || value === '' ? 0 : value).replace(/,/g, ''))
  return isNaN(amount) ? 0 : amount
}

function getSiteCredit (balance, site) {
  return toCreditNumber(balance && balance[site] && balance[site].credit)
}

function getUfaPromotionCredit (ufa) {
  if (!ufa) return 0
  const baseBalance = ufa.balance !== undefined ? ufa.balance : ufa.credit
  return toCreditNumber(baseBalance) +
    toCreditNumber(ufa.outstandingSport) +
    toCreditNumber(ufa.in_game_credit)
}

function getUfaPendingPlayTotal (ufa) {
  if (!ufa) return 0
  return toCreditNumber(ufa.outstandingSport) + toCreditNumber(ufa.in_game_credit)
}

function getPromotionBalanceTotal (balance) {
  if (!balance) return 0
  return toCreditNumber(balance.credit) +
    getSiteCredit(balance, 'amb') +
    getSiteCredit(balance, 'cnnwin') +
    getSiteCredit(balance, 'lunarbet') +
    getSiteCredit(balance, 'imi') +
    getSiteCredit(balance, 'betflix') +
    getSiteCredit(balance, 'edmbet') +
    getUfaPromotionCredit(balance.ufa)
}

function getPromotionPendingPlayTotal (balance) {
  if (!balance) return 0
  return getUfaPendingPlayTotal(balance.ufa)
}

function getPromotionOutstandingSites (balance) {
  if (!balance) return []
  const providers = [
    { site: 'amb', label: 'AMB', credit: getSiteCredit(balance, 'amb') },
    { site: 'cnnwin', label: 'CNNWIN', credit: getSiteCredit(balance, 'cnnwin') },
    { site: 'lunarbet', label: 'Lunarbet', credit: getSiteCredit(balance, 'lunarbet') },
    { site: 'imi', label: 'IMI', credit: getSiteCredit(balance, 'imi') },
    { site: 'betflix', label: 'Betflix', credit: getSiteCredit(balance, 'betflix') },
    { site: 'edmbet', label: 'EDMBET', credit: getSiteCredit(balance, 'edmbet') },
    {
      site: 'ufa',
      label: 'UFA',
      credit: toCreditNumber(balance.ufa && (balance.ufa.balance !== undefined ? balance.ufa.balance : balance.ufa.credit))
    }
  ]
  return providers
    .map(provider => ({ ...provider, credit: Number(provider.credit.toFixed(2)) }))
    .filter(provider => provider.credit > 0)
}

function isBonusClaimBalanceExempt (type) {
  return ['partner', 'cashback', 'redeem', 'reward', 'rewards'].includes(type)
}

function hasActiveBonusCondition (data) {
  if (!data || data.promoWithdrawn === true) return false
  const bonusToday = data.bonustoday || {}
  const bonustype = String(data.bonustype || bonusToday.type || '').toLowerCase()
  const noWithdrawCondition = bonusToday.noWithdrawCondition === true ||
    ['partner', 'cashback', 'redeem', 'reward', 'rewards'].includes(bonustype)
  if (noWithdrawCondition) return false

  const manualBonus = data.manual_bonus === true ||
    data.manual_bonus === 'true' ||
    data.manual_bonus === 'Y' ||
    data.manual_bonus === '1'
  if (manualBonus) return true

  const promotion = data.bonustoday && data.bonustoday.promotion
  const hasPromotionConfig = typeof promotion === 'object'
    ? Object.keys(promotion || {}).length > 0
    : Boolean(promotion)
  return toCreditNumber(data.turncredit) > 0 || toCreditNumber(data.canwithdraw) > 0 || hasPromotionConfig
}

function isCompletedNoPromoDepositAfterBonus (data, bonusCreatedate) {
  if (!data || data.status !== 'ดำเนินการแล้ว' || data.type !== 'ฝาก') return false
  if (String(data.createdate || '') <= String(bonusCreatedate || '')) return false
  if (toCreditNumber(data.qty) <= 0) return false

  const promotion = data.bonustoday && data.bonustoday.promotion
  const hasPromotionConfig = typeof promotion === 'object'
    ? Object.keys(promotion || {}).length > 0
    : Boolean(promotion)

  return toCreditNumber(data.bonus) <= 0 &&
    toCreditNumber(data.turncredit) <= 0 &&
    toCreditNumber(data.canwithdraw) <= 0 &&
    !hasPromotionConfig
}

function isCompletedFullWithdrawalAfterBonus (data, bonusCreatedate) {
  if (!data || data.status !== 'ดำเนินการแล้ว' || data.type !== 'ถอน') return false
  if (String(data.createdate || '') <= String(bonusCreatedate || '')) return false

  const qty = toCreditNumber(data.qty)
  const creditAll = toCreditNumber(data.creditall)
  if (qty <= 0) return false

  return creditAll <= 0 || Math.abs(qty - creditAll) <= 1
}

async function getLatestActiveBonusCondition (userKey) {
  const [depositSnapshot, withdrawalSnapshot] = await Promise.all([
    firebase.firestore().collection('krungsri')
      .where('userkey', '==', userKey)
      .where('status', '==', 'ดำเนินการแล้ว')
      .orderBy('createdate', 'desc')
      .limit(20)
      .get(),
    firebase.firestore().collection('history')
      .where('userkey', '==', userKey)
      .orderBy('createdate', 'desc')
      .limit(30)
      .get()
  ])

  const records = depositSnapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() || {} }))
  const withdrawals = withdrawalSnapshot.docs.map((doc) => doc.data() || {})

  return records.find((record) => {
    if (!hasActiveBonusCondition(record.data)) return false

    const bonusCreatedate = record.data.createdate || ''
    const hasNoPromoDepositAfterBonus = records.some((deposit) => {
      return deposit.id !== record.id && isCompletedNoPromoDepositAfterBonus(deposit.data, bonusCreatedate)
    })
    const hasFullWithdrawalAfterBonus = withdrawals.some((withdrawal) => {
      return isCompletedFullWithdrawalAfterBonus(withdrawal, bonusCreatedate)
    })

    return !hasNoPromoDepositAfterBonus && !hasFullWithdrawalAfterBonus
  }) || null
}

async function getPromotionBalanceGuard (userKey) {
  const { data } = await axios.get('https://api.vr1game-v2.online/api/getbalance/' + userKey)
  const totalBalance = getPromotionBalanceTotal(data)
  const pendingPlayTotal = getPromotionPendingPlayTotal(data)
  const outstandingSites = getPromotionOutstandingSites(data)
  const outstandingGameTotal = Number(outstandingSites.reduce((total, site) => total + site.credit, 0).toFixed(2))
  const balanceStatus = Boolean(data && data.status)
  return {
    blocked: Boolean(balanceStatus && totalBalance >= PROMOTION_BALANCE_LIMIT),
    balanceStatus,
    limit: PROMOTION_BALANCE_LIMIT,
    totalBalance,
    promotionBalanceTotal: totalBalance,
    pendingPlayTotal,
    outstandingSites,
    outstandingGameTotal,
    balance: data
  }
}

function parseSecurePayVisibleCodes (value) {
  if (Array.isArray(value)) {
    return value.map(item => {
      if (item && typeof item === 'object') return String(item.value || '').trim().toUpperCase()
      return String(item || '').trim().toUpperCase()
    }).filter(Boolean)
  }

  return String(value || '')
    .split(',')
    .map(code => code.trim().toUpperCase())
    .filter(Boolean)
}

function isSecurePayOpenNow (bank) {
  const displayTime = bank && bank.display_time
  if (!displayTime || !displayTime.start_time || !displayTime.end_time) return true

  const current = moment(moment().tz('Asia/Bangkok').format('HH:mm'), 'HH:mm')
  const start = moment(displayTime.start_time, 'HH:mm')
  const end = moment(displayTime.end_time, 'HH:mm')
  if (end.isBefore(start)) return current.isSameOrAfter(start) || current.isSameOrBefore(end)
  return current.isBetween(start, end, undefined, '[]')
}

function canUserAccessSecurePayDeposit (bank, user) {
  if (!bank || !user) return false

  const isSecurePay = bank.bank_name === 'securepay' ||
    bank.payment_provider === 'securepay' ||
    bank.type_deposit === 'securepay'
  const statusEnabled = bank.status === true || bank.status === 'Y'
  const usage = String(bank.payment_usage || 'both').trim().toLowerCase()
  const depositEnabled = usage === 'deposit' || usage === 'both'
  const visibleCodes = [
    ...parseSecurePayVisibleCodes(bank.visible_to_codes),
    ...parseSecurePayVisibleCodes(bank.visible_to)
  ]
  const userCode = String(user.code || '').trim().toUpperCase()
  const visibleToUser = Boolean(userCode) && (
    visibleCodes.length === 0 || visibleCodes.includes('*') || visibleCodes.includes(userCode)
  )

  return isSecurePay && statusEnabled && depositEnabled && isSecurePayOpenNow(bank) && visibleToUser
}

export default {
  async setUser ({ commit, state, dispatch }, user) {
    if (user.key) {
      const _user = (await firebase.database().ref(`/users/${user.key}`).once('value')).val()
      if((!_user) || (_user && _user.status === 'N') || (user.keygen !== _user.keygen)){
        firebase.auth().signOut()
        localStorage.removeItem('userData')
        router.replace('/login')
      }
      _user.key = user.key
      commit('SET_USER', _user)
      dispatch('settings/resolveAdminContactLineLink', _user, { root: true }).catch((error) => {
        console.error('Error resolving admin contact line link:', error)
      })
      if (state.ref) state.ref.off()
      state.ref = firebase.database().ref('users/' + user.key)
      state.ref.on('child_changed', snapshot => {
        if (snapshot.key === 'credit' && state.user.credit < snapshot.val()) {
          commit('SET_CREDIT_INCREASE', true)
        } else if (snapshot.key === 'credit' && state.user.credit > snapshot.val()) {
          commit('SET_CREDIT_DECREASE', true)
        } else if (snapshot.key === 'coin') {
          commit('SET_COIN_VOICE', true)
        } else if (snapshot.key === 'status' && snapshot.val() === 'N') {
          if (state.user.pending_approval) {
            const Swal = require('sweetalert2')
            Swal.fire({
              icon: 'error',
              title: 'ขออภัย',
              html: 'ข้อมูลที่ใช้สมัครสมาชิก ไม่สามารถใช้งานได้<br>รบกวนติดต่อแอดมินที่ line id <strong>@vr1game</strong> เพื่อแจ้งปัญหานะคะ'
            }).then(() => {
              firebase.auth().signOut()
              localStorage.removeItem('userData')
              router.replace('/login')
            })
          } else {
            firebase.auth().signOut()
            localStorage.removeItem('userData')
            router.replace('/login')
          }
        } else if (snapshot.key === 'keygen' && user.keygen !== snapshot.val()) {
          firebase.auth().signOut()
          localStorage.removeItem('userData')
          router.replace('/login')
        }
        state.user[snapshot.key] = snapshot.val()
        commit('SET_USER', _user)
        if (snapshot.key === 'userFriendId') {
          dispatch('settings/resolveAdminContactLineLink', state.user, { root: true }).catch((error) => {
            console.error('Error resolving admin contact line link:', error)
          })
        }
      })
      // Listen for fields being removed (e.g. admin approves pending_approval)
      state.ref.on('child_removed', snapshot => {
        if (snapshot.key === 'pending_approval' && state.user.status !== 'N') {
          const Swal = require('sweetalert2')
          Swal.fire({
            icon: 'success',
            title: 'สมัครสำเร็จ!',
            html: 'ระบบทำรายการสมัครสำเร็จแล้วค่ะ สามารถเริ่มทำรายการฝากได้เลยค่ะ<br>ขอให้สนุก และเฮงๆปังๆ ในทุกการเดิมพันนะคะ'
          })
        }
        delete state.user[snapshot.key]
        commit('SET_USER', { ...state.user })
        if (snapshot.key === 'userFriendId') {
          dispatch('settings/resolveAdminContactLineLink', state.user, { root: true }).catch((error) => {
            console.error('Error resolving admin contact line link:', error)
          })
        }
      })
    } else {
      firebase.auth().signOut()
      localStorage.removeItem('userData')
      router.replace('/login')
    }
  },
  async getBanners ({ commit }) {
    const banners = await getSessionCachedData('active-banners', async () => {
      const snapshot = await firebase.database().ref('/banners').orderByChild('status').equalTo('Y').once('value')
      return snapshot.val() || {}
    })
    commit('SET_BANNERS', _.orderBy(banners, ['order'], ['asc']))
  },
  async getSites ({ commit, state }) {
    // ปิด listener เก่าก่อน (ถ้ามี) เพื่อป้องกัน memory leak
    if (state.refsite) {
      state.refsite.off()
      state.refsite = null
    }

    // สร้าง reference เดียวกันสำหรับทั้ง initial load และ real-time updates
    const sitesRef = firebase.database().ref('/sites').orderByChild('used').equalTo(true)

    try {
      // ดึงข้อมูลครั้งแรก
      const snapshot = await sitesRef.once('value')
      const sites = snapshot.val()

      // ตัวแปรเก็บสถานะว่าได้ load ข้อมูลครั้งแรกแล้วหรือยัง
      let isInitialLoad = true

      if (sites) {
        const sortedSites = _.orderBy(Object.keys(sites).map(key => ({
          key,
          ...sites[key]
        })), ['order'], ['asc'])
        commit('SET_SITES', sortedSites)
      } else {
        commit('SET_SITES', [])
      }

      // หลังจาก initial load เสร็จแล้วค่อยตั้ง real-time listeners
      setTimeout(() => {
        isInitialLoad = false
      }, 1000)

      // ตั้งค่า real-time listener สำหรับการเปลี่ยนแปลง
      state.refsite = sitesRef

      state.refsite.on('child_changed', snapshot => {
        if (!isInitialLoad) {
          const updatedSite = { key: snapshot.key, ...snapshot.val() }
          const currentSites = [...state.sites]
          const index = currentSites.findIndex(site => site.key === snapshot.key)

          if (index !== -1) {
            currentSites[index] = updatedSite
            commit('SET_SITES', _.orderBy(currentSites, ['order'], ['asc']))
          }
        }
      })

      // เพิ่ม listener สำหรับ site ใหม่ (ไม่ให้ trigger ระหว่าง initial load)
      state.refsite.on('child_added', snapshot => {
        if (!isInitialLoad && snapshot.val().used === true) {
          const newSite = { key: snapshot.key, ...snapshot.val() }
          const currentSites = [...state.sites]
          
          // เช็คว่ามี site นี้อยู่แล้วหรือไม่
          const existingIndex = currentSites.findIndex(site => site.key === snapshot.key)
          if (existingIndex === -1) {
            currentSites.push(newSite)
            commit('SET_SITES', _.orderBy(currentSites, ['order'], ['asc']))
          }
        }
      })

      // เพิ่ม listener สำหรับ site ที่ถูกลบ
      state.refsite.on('child_removed', snapshot => {
        if (!isInitialLoad) {
          const currentSites = state.sites.filter(site => site.key !== snapshot.key)
          commit('SET_SITES', currentSites)
        }
      })
    } catch (error) {
      console.error('Error fetching sites:', error)
      commit('SET_SITES', [])
    }
  },
  async refreshCreditAll ({ commit, state }, check) {
    let key = state.user.key
    if (!key) {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        key = _user.key
      }
    }
    const { data } = await axios.get('https://api.vr1game-v2.online/api/getbalance/' + key)
    if (data.status) {
      if (check && check.checkamb && data.credit && data.amb && data.amb.credit && data.credit === data.amb.credit) {
        data.amb.credit = 0
      }
      commit('SET_SITES_CREDIT', data)
    }
  },
  async moveCreditToWallet ({ dispatch, state }) {
    let key = state.user.key
    if (!key) {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        key = _user.key
      }
    }
    if (!key) return { status: false, error: 'ไม่พบผู้ใช้งาน' }

    const { data } = await axios.get('https://api.vr1game-v2.online/qmovetowallet/move/' + key)
    if (!data || data.status !== true) {
      return data || { status: false, error: 'ไม่สามารถโยกเครดิตกลับเข้ากระเป๋าหลักได้' }
    }
    await dispatch('refreshCreditAll', { checkamb: true })
    return data
  },
  async getLastBonus ({ commit }, user) {
    const response = { status: false, bonus: 0, canwithdraw: 0, turncredit: 0, promotionName: '', backbonus: false }
    try {
      const balanceGuard = await getPromotionBalanceGuard(user.key)
      if (!balanceGuard.blocked) {
        response.status = true
        commit('SET_LAST_BONUS', response)
        return
      }
    } catch (error) {
      console.error('Error checking promotion balance before last bonus:', error)
    }
    await firebase.firestore().collection('krungsri').where('userkey', '==', user.key).where('status', '==', 'ดำเนินการแล้ว').orderBy('createdate', 'desc').limit(5).get().then((snapshot) => {
      const latestActiveBonus = snapshot.docs.find((doc) => {
        const data = doc.data() || {}
        return data.promoWithdrawn !== true && (!data.type || data.type === 'ฝาก')
      })
      if (latestActiveBonus) {
        const bonusToday = latestActiveBonus.data().bonustoday || {}
        const promotion = bonusToday.promotion || {}
        response.bonus = (bonusToday.bonus !== undefined) ? bonusToday.bonus : ((latestActiveBonus.data().bonus) ? latestActiveBonus.data().bonus : 0)
        response.canwithdraw = (latestActiveBonus.data().canwithdraw) ? latestActiveBonus.data().canwithdraw : 0
        response.turncredit = (latestActiveBonus.data().turncredit) ? latestActiveBonus.data().turncredit : 0
        response.promotionName = promotion.name || bonusToday.name || ''
        response.backbonus = Boolean(promotion.backbonus || bonusToday.backbonus)
      }
    })
    response.status = true
    commit('SET_LAST_BONUS', response)
  },
  async preparePromoWithdrawal ({ commit }, user) {
    const response = {
      status: false,
      activePromotion: false,
      withdrawBlocked: false,
      bonus: 0,
      canwithdraw: 0,
      turncredit: 0,
      promotionName: '',
      backbonus: false,
      blockingMessage: ''
    }

    try {
      const latestActiveBonus = await getLatestActiveBonusCondition(user.key)
      if (!latestActiveBonus) {
        response.status = true
        commit('SET_LAST_BONUS', response)
        return response
      }

      const data = latestActiveBonus.data || {}
      const bonusToday = data.bonustoday || {}
      const promotion = bonusToday.promotion || {}
      response.activePromotion = true
      response.bonus = bonusToday.bonus !== undefined ? bonusToday.bonus : toCreditNumber(data.bonus)
      response.canwithdraw = toCreditNumber(data.canwithdraw)
      response.turncredit = toCreditNumber(data.turncredit)
      response.promotionName = promotion.name || bonusToday.name || data.promotionName || data.bonustype || ''
      response.backbonus = Boolean(promotion.backbonus || bonusToday.backbonus)

      const balanceGuard = await getPromotionBalanceGuard(user.key)
      Object.assign(response, balanceGuard)

      if (!balanceGuard.balanceStatus) {
        response.withdrawBlocked = true
        response.blockingMessage = 'ไม่สามารถตรวจสอบยอดเครดิตก่อนถอนได้ กรุณาลองใหม่อีกครั้ง'
      } else if (balanceGuard.pendingPlayTotal > 0) {
        response.withdrawBlocked = true
        response.blockingMessage = 'ยังมีรายการเล่นค้าง กรุณารอให้รายการตัดผลก่อน แล้วกดโยกเงินออกทั้งหมดกลับเข้ากระเป๋าหลัก'
      } else if (balanceGuard.outstandingGameTotal > 0) {
        const siteNames = balanceGuard.outstandingSites.map(site => site.label).join(', ')
        response.withdrawBlocked = true
        response.blockingMessage = `ยังมีเครดิตคงค้างในเกม ${siteNames} รวม ${balanceGuard.outstandingGameTotal.toLocaleString()} บาท กรุณากดโยกเงินออกทั้งหมดกลับเข้ากระเป๋าหลักก่อน`
      } else if (response.turncredit > 0 && toCreditNumber(balanceGuard.balance && balanceGuard.balance.credit) < response.turncredit) {
        response.withdrawBlocked = true
        response.blockingMessage = `คุณต้องมียอดเครดิตอย่างน้อย ${response.turncredit.toLocaleString()} บาทก่อนถอน`
      }

      response.status = true
    } catch (error) {
      console.error('Error preparing promotion withdrawal:', error)
      response.activePromotion = true
      response.withdrawBlocked = true
      response.blockingMessage = 'ไม่สามารถตรวจสอบเงื่อนไขโปรก่อนถอนได้ กรุณาลองใหม่อีกครั้ง'
    }

    commit('SET_LAST_BONUS', response)
    return response
  },
  async checkPromotionBalance ({ state }) {
    const userKey = state.user?.key
    if (!userKey) return { status: false, blocked: false }

    const balanceGuard = await getPromotionBalanceGuard(userKey)
    return { status: true, ...balanceGuard }
  },
  async checkBonusClaimEligibility ({ state }, payload = {}) {
    const type = typeof payload === 'string' ? payload : payload.type
    const userKey = state.user?.key

    if (!userKey) {
      return { status: false, allowed: false, blocked: false, reason: 'NO_USER' }
    }

    if (isBonusClaimBalanceExempt(type)) {
      return { status: true, allowed: true, blocked: false, reason: '' }
    }

    try {
      const balanceGuard = await getPromotionBalanceGuard(userKey)
      if (balanceGuard.blocked) {
        return {
          status: true,
          allowed: false,
          blocked: true,
          reason: 'BALANCE_OVER_LIMIT',
          ...balanceGuard
        }
      }

      return {
        status: true,
        allowed: true,
        blocked: false,
        reason: '',
        ...balanceGuard
      }
    } catch (error) {
      console.error('Error checking bonus claim eligibility:', error)
      return { status: false, allowed: false, blocked: false, reason: 'BALANCE_CHECK_FAILED' }
    }
  },
  async checkSecurePayDepositAccess ({ state }) {
    const user = state.user

    if (!user || !user.key) {
      return { status: false, allowed: false, reason: 'NO_USER', bank: null }
    }

    try {
      const snapshot = await firebase.database().ref('/banks/securepay').once('value')
      const bank = snapshot.val()
      const allowed = canUserAccessSecurePayDeposit(bank, user)
      return {
        status: true,
        allowed,
        reason: allowed ? '' : 'SECUREPAY_NOT_AVAILABLE',
        bank: bank ? { ...bank, key: 'securepay', id: 'securepay' } : null
      }
    } catch (error) {
      console.error('Error checking SecurePay deposit access:', error)
      return { status: false, allowed: false, reason: 'SECUREPAY_CHECK_FAILED', bank: null }
    }
  },
  async checkActivePromotionDepositBlock ({ state }) {
    const userKey = state.user?.key

    if (!userKey) {
      return { status: false, allowed: false, blocked: false, reason: 'NO_USER' }
    }

    try {
      const activeBonus = await getLatestActiveBonusCondition(userKey)
      if (!activeBonus) {
        return { status: true, allowed: true, blocked: false, reason: '' }
      }

      const balanceGuard = await getPromotionBalanceGuard(userKey)
      const balanceStatus = balanceGuard && balanceGuard.balance && balanceGuard.balance.status === true
      const bonusToday = activeBonus.data.bonustoday || {}
      const promotion = bonusToday.promotion || {}
      const activeBonusMetadata = {
        sourceId: activeBonus.id,
        promotionName: promotion.name || bonusToday.name || '',
        bonus: toCreditNumber(activeBonus.data.bonus),
        turncredit: toCreditNumber(activeBonus.data.turncredit),
        canwithdraw: toCreditNumber(activeBonus.data.canwithdraw)
      }

      if (!balanceStatus) {
        return {
          status: true,
          allowed: false,
          blocked: true,
          reason: 'BALANCE_CHECK_FAILED',
          ...activeBonusMetadata,
          ...balanceGuard
        }
      }

      if (balanceGuard.blocked) {
        return {
          status: true,
          allowed: false,
          blocked: true,
          reason: 'ACTIVE_PROMO_DEPOSIT_BLOCKED',
          ...activeBonusMetadata,
          ...balanceGuard
        }
      }

      return {
        status: true,
        allowed: true,
        blocked: false,
        reason: 'BALANCE_BELOW_LIMIT',
        ...activeBonusMetadata,
        ...balanceGuard
      }
    } catch (error) {
      console.error('Error checking active promotion deposit block:', error)
      return { status: false, allowed: false, blocked: true, reason: 'ACTIVE_PROMO_CHECK_FAILED' }
    }
  },
  async checkPromotions ({ commit, state }) {
    let promotions = {}
    let pro = false

    if (state.user.disablepro && state.user.disablepro.promotion === false) {
      commit('SET_PROMOTIONS', promotions)
      return { status: true, blocked: false }
    }

    const userKey = state.user?.key

    // ตรวจสอบว่า userKey มีค่าหรือไม่
    if (!userKey) {
      console.error('Error: userKey is undefined')
      commit('SET_PROMOTIONS', {})
      commit('SET_GOT_PRO', false)
      return { status: false, blocked: false }
    }

    const today = moment().tz('Asia/Bangkok')
    const todayString = today.format('YYYY-MM-DD')
    const currentTime = today.format('HHmm')

    try {
      // ดึงข้อมูล promotions ทั้งหมดที่ active พร้อมกัน
      const [krungsriSnapshot, allPromotionsSnapshot] = await Promise.all([
        firebase.firestore().collection('krungsri')
          .where('userkey', '==', userKey)
          .orderBy('createdate', 'asc')
          .limit(2)
          .get(),
        firebase.firestore().collection('promotions')
          .where('status', '==', true)
          .get()
      ])

      // แปลง promotions เป็น object เพื่อใช้งานง่าย
      const promotionsByType = {}
      allPromotionsSnapshot.forEach((doc) => {
        const data = doc.data()
        const type = data.type
        if (!promotionsByType[type]) {
          promotionsByType[type] = []
        }
        promotionsByType[type].push({ ...data, id: doc.id })
      })

      // ตรวจสอบ สมัครสมาชิกใหม่
      let isNewMember = false
      if (!krungsriSnapshot.size) {
        isNewMember = true
      } else if (krungsriSnapshot.size === 1) {
        krungsriSnapshot.forEach((doc) => {
          if (doc.data().bonus && (!doc.data().qty || doc.data().qty === 0)) {
            isNewMember = true
          }
        })
      }
      if (isNewMember && promotionsByType['สมัครสมาชิกใหม่']) {
        promotions = promotionsByType['สมัครสมาชิกใหม่'][0]
        pro = true
      }

      // ตรวจสอบ โปรพิเศษ (ถ้ายังไม่ได้โปร)
      if (!pro && promotionsByType['โปรพิเศษ']) {
        for (const promotion of promotionsByType['โปรพิเศษ']) {
          const starttime = promotion.starttime?.replace(':', '') || '0000'
          const endtime = promotion.endtime?.replace(':', '') || '2359'

          if (Number(currentTime) >= Number(starttime) && Number(currentTime) <= Number(endtime)) {
            // ตรวจสอบ maxreceive ถ้ามี
            if (promotion.maxreceive && promotion.id) {
              const promotionUsersSnapshot = await firebase.firestore()
                .collection('promotion_users')
                .where('userkey', '==', userKey)
                .where('promotionId', '==', promotion.id)
                .where('date', '==', todayString)
                .get()

              if (promotionUsersSnapshot.size < promotion.maxreceive) {
                promotions = promotion
                pro = true
                break
              }
            } else if (!promotion.maxreceive) {
              promotions = promotion
              pro = true
              break
            }
          }
        }
      }

      // ตรวจสอบ ฝากแรกของวัน (ถ้ายังไม่ได้โปร)
      if (!pro && promotionsByType['ฝากแรกของวัน']) {
        const todayDepositsSnapshot = await firebase.firestore()
          .collection('krungsri')
          .where('userkey', '==', userKey)
          .where('createdate', '>=', todayString + ' 00:00:00')
          .limit(2)
          .get()

        let isFirstDepositToday = false
        if (!todayDepositsSnapshot.size) {
          isFirstDepositToday = true
        } else if (todayDepositsSnapshot.size === 1) {
          todayDepositsSnapshot.forEach((doc) => {
            if (doc.data().bonus && (!doc.data().qty || doc.data().qty === 0)) {
              isFirstDepositToday = true
            }
          })
        }

        if (isFirstDepositToday) {
          promotions = promotionsByType['ฝากแรกของวัน'][0]
          pro = true
        }
      }

      // ตรวจสอบ โปรช่วงเวลา (ถ้ายังไม่ได้โปร)
      if (!pro && promotionsByType['โปรช่วงเวลา']) {
        for (const promotion of promotionsByType['โปรช่วงเวลา']) {
          const starttime = promotion.starttime?.replace(':', '') || '0000'
          const endtime = promotion.endtime?.replace(':', '') || '2359'

          if (Number(currentTime) >= Number(starttime) && Number(currentTime) <= Number(endtime)) {
            promotions = promotion
            pro = true
            break
          }
        }
      }

      // ตรวจสอบ โปรฝากทั้งวัน (ถ้ายังไม่ได้โปร)
      if (!pro && promotionsByType['ฝากทั้งวัน']) {
        promotions = promotionsByType['ฝากทั้งวัน'][0]
        pro = true
      }

      // ตรวจสอบว่าได้โปรแล้วหรือยัง
      let gotpro = false
      if (pro) {
        const userSnapshot = await firebase.database().ref(`/users/${userKey}`).once('value')
        const userData = userSnapshot.val()
        gotpro = (userData && userData.bonus === 'Y')
      }
      commit('SET_PROMOTIONS', promotions)
      commit('SET_GOT_PRO', gotpro)
      return { status: true, blocked: false }
    } catch (error) {
      console.error('Error checking promotions:', error)
      commit('SET_PROMOTIONS', {})
      commit('SET_GOT_PRO', false)
      return { status: false, blocked: false }
    }
  },
  async getRewards ({ commit }) {
    const rewards = (await firebase.database().ref('/rewards').orderByChild('status').equalTo('Y').once('value')).val()
    for (const x in rewards) {
      rewards[x].key = x
    }
    commit('SET_REWARDS', _.orderBy(rewards, ['point'], ['asc']))
  },
}
